import React, { useEffect, useMemo, useRef, useState } from "react";
import { api, formatApiError } from "../../../lib/api";
import { toast } from "sonner";
import {
  Loader2, ChevronLeft, Monitor, Smartphone, Send, RotateCcw, Zap, Lock, CheckCircle2, Pencil,
} from "lucide-react";

const CATEGORY_ORDER = ["Réservations", "Devis", "Contrats", "Facturation", "Rappels automatiques", "Compte"];

export default function EmailTemplatesManager() {
  const [templates, setTemplates] = useState(null);
  const [selected, setSelected] = useState(null);

  const load = () => api.get("/vendor/emails/templates").then(({ data }) => {
    setTemplates(data.templates);
    if (selected) setSelected(data.templates.find((t) => t.key === selected.key) || null);
  }).catch(() => setTemplates([]));
  useEffect(() => { load(); }, []); // eslint-disable-line

  if (templates === null) return <div className="py-16 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-violet-600" /></div>;

  if (selected) {
    return <TemplateEditor template={selected} onBack={() => setSelected(null)} onChanged={(list) => {
      setTemplates(list);
      setSelected(list.find((t) => t.key === selected.key) || null);
    }} />;
  }

  const grouped = CATEGORY_ORDER.map((cat) => ({ cat, items: templates.filter((t) => t.category === cat) })).filter((g) => g.items.length);

  return (
    <div className="space-y-6" data-testid="email-templates-list">
      {grouped.map(({ cat, items }) => (
        <div key={cat}>
          <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-2">{cat}</div>
          <div className="space-y-1.5">
            {items.map((t) => (
              <button
                key={t.key} onClick={() => setSelected(t)}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-xl border border-slate-200 bg-white hover:border-violet-300 hover:shadow-sm transition-all text-left"
                data-testid={`email-template-${t.key}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-slate-800">{t.label}</span>
                    {t.automated && <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-amber-50 text-amber-600 text-[10px] font-bold"><Zap className="h-2.5 w-2.5" /> Auto</span>}
                    {t.protected && <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-slate-100 text-slate-500 text-[10px] font-bold"><Lock className="h-2.5 w-2.5" /> Requis</span>}
                    {t.is_customized && <span className="px-1.5 py-0.5 rounded bg-violet-50 text-violet-600 text-[10px] font-bold">Personnalisé</span>}
                  </div>
                  <div className="text-[11px] text-slate-400 truncate mt-0.5">{t.subject}</div>
                </div>
                <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold shrink-0 ${t.enabled ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-400"}`}>
                  {t.enabled ? "Actif" : "Désactivé"}
                </span>
                <Pencil className="h-3.5 w-3.5 text-slate-300 shrink-0" />
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function TemplateEditor({ template: t, onBack, onChanged }) {
  const [subject, setSubject] = useState(t.subject);
  const [body, setBody] = useState(t.body);
  const [enabled, setEnabled] = useState(t.enabled);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [preview, setPreview] = useState(null);
  const [device, setDevice] = useState("desktop");
  const bodyRef = useRef(null);
  const debounceRef = useRef(null);

  const dirty = subject !== t.subject || body !== t.body || enabled !== t.enabled;
  const isDefault = subject === t.default_subject && body === t.default_body;

  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      api.post(`/vendor/emails/templates/${t.key}/preview`, { subject, body })
        .then(({ data }) => setPreview(data)).catch(() => {});
    }, 400);
    return () => clearTimeout(debounceRef.current);
  }, [subject, body, t.key]);

  const insertVar = (v) => {
    const el = bodyRef.current;
    const token = `{${v}}`;
    if (el && document.activeElement === el) {
      const s = el.selectionStart, e = el.selectionEnd;
      setBody(body.slice(0, s) + token + body.slice(e));
      requestAnimationFrame(() => { el.focus(); el.setSelectionRange(s + token.length, s + token.length); });
    } else {
      setBody(body + token);
    }
  };

  const save = async (extra = {}) => {
    setSaving(true);
    try {
      const { data } = await api.put(`/vendor/emails/templates/${t.key}`, { subject, body, enabled, ...extra });
      toast.success("Modèle enregistré");
      onChanged(data.templates);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Erreur"); }
    setSaving(false);
  };

  const restore = async () => {
    if (!window.confirm("Restaurer le modèle par défaut ? Vos personnalisations d'objet et de contenu seront perdues.")) return;
    try {
      const { data } = await api.post(`/vendor/emails/templates/${t.key}/restore`);
      toast.success("Modèle par défaut restauré");
      onChanged(data.templates);
      const fresh = data.templates.find((x) => x.key === t.key);
      if (fresh) { setSubject(fresh.subject); setBody(fresh.body); setEnabled(fresh.enabled); }
    } catch { toast.error("Erreur"); }
  };

  const sendTest = async () => {
    setTesting(true);
    try {
      const { data } = await api.post(`/vendor/emails/templates/${t.key}/test`);
      toast.success(data.message);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Erreur"); }
    setTesting(false);
  };

  return (
    <div data-testid={`email-editor-${t.key}`}>
      <button onClick={onBack} className="inline-flex items-center gap-1 text-xs font-semibold text-slate-500 hover:text-slate-800 mb-4" data-testid="email-editor-back">
        <ChevronLeft className="h-3.5 w-3.5" /> Tous les modèles
      </button>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <h3 className="text-base font-bold text-slate-900 mr-auto">{t.label}</h3>
        <button onClick={sendTest} disabled={testing} className="inline-flex items-center gap-1.5 px-3 h-8 rounded-lg border border-violet-200 bg-violet-50 text-violet-700 text-[11px] font-bold hover:bg-violet-100 disabled:opacity-50" data-testid="email-test-btn">
          {testing ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />} M'envoyer un email de test
        </button>
        {!isDefault && (
          <button onClick={restore} className="inline-flex items-center gap-1.5 px-3 h-8 rounded-lg border border-slate-200 text-slate-500 text-[11px] font-semibold hover:text-slate-800" data-testid="email-restore-btn">
            <RotateCcw className="h-3 w-3" /> Restaurer le défaut
          </button>
        )}
      </div>

      <div className="grid lg:grid-cols-2 gap-5">
        {/* Edition */}
        <div className="space-y-4">
          {!t.protected && (
            <label className="flex items-center justify-between rounded-xl border border-slate-200 bg-white px-4 py-3 cursor-pointer">
              <span className="text-xs font-semibold text-slate-700">Modèle actif</span>
              <button
                type="button" role="switch" aria-checked={enabled}
                onClick={() => setEnabled(!enabled)}
                className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${enabled ? "bg-violet-600" : "bg-slate-300"}`}
                data-testid="email-enabled-toggle"
              >
                <span className={`inline-block h-3.5 w-3.5 rounded-full bg-white transform transition-transform ${enabled ? "translate-x-[18px]" : "translate-x-1"}`} />
              </button>
            </label>
          )}
          <div>
            <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wide mb-1.5">Objet</label>
            <input
              value={subject} onChange={(e) => setSubject(e.target.value)}
              className="w-full h-10 px-3 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500/30 focus:border-violet-400"
              data-testid="email-subject-input"
            />
          </div>
          <div>
            <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wide mb-1.5">Message</label>
            <textarea
              ref={bodyRef} value={body} onChange={(e) => setBody(e.target.value)} rows={12}
              className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm leading-relaxed focus:outline-none focus:ring-2 focus:ring-violet-500/30 focus:border-violet-400 font-mono text-[13px]"
              data-testid="email-body-input"
            />
          </div>
          <div>
            <div className="text-[11px] font-bold text-slate-500 uppercase tracking-wide mb-1.5">Variables disponibles <span className="font-normal normal-case text-slate-400">(cliquez pour insérer)</span></div>
            <div className="flex flex-wrap gap-1.5" data-testid="email-variables">
              {t.variables.map((v) => (
                <button key={v} onMouseDown={(e) => { e.preventDefault(); insertVar(v); }}
                  className="px-2 py-1 rounded-lg bg-slate-100 hover:bg-violet-100 hover:text-violet-700 text-slate-600 text-[11px] font-mono transition-colors"
                  data-testid={`email-var-${v}`}>
                  {`{${v}}`}
                </button>
              ))}
            </div>
          </div>
          <button onClick={() => save()} disabled={!dirty || saving}
            className="inline-flex items-center gap-2 px-5 h-10 rounded-xl bg-slate-900 text-white text-xs font-bold hover:bg-slate-800 disabled:opacity-40"
            data-testid="email-save-btn">
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5" />} Enregistrer le modèle
          </button>
        </div>

        {/* Aperçu */}
        <div className="rounded-2xl border border-slate-200 bg-slate-50 overflow-hidden self-start" data-testid="email-preview">
          <div className="flex items-center gap-2 px-4 h-11 border-b border-slate-200 bg-white">
            <span className="text-[11px] font-bold text-slate-600">Aperçu</span>
            {preview && <span className="text-[10px] text-slate-400 truncate flex-1">Objet : {preview.subject}</span>}
            <button onClick={() => setDevice("desktop")} className={`h-7 w-7 rounded-lg flex items-center justify-center ${device === "desktop" ? "bg-slate-900 text-white" : "text-slate-400 hover:bg-slate-100"}`} data-testid="email-preview-desktop"><Monitor className="h-3.5 w-3.5" /></button>
            <button onClick={() => setDevice("mobile")} className={`h-7 w-7 rounded-lg flex items-center justify-center ${device === "mobile" ? "bg-slate-900 text-white" : "text-slate-400 hover:bg-slate-100"}`} data-testid="email-preview-mobile"><Smartphone className="h-3.5 w-3.5" /></button>
          </div>
          <div className={device === "mobile" ? "flex justify-center py-3 bg-slate-100" : ""}>
            {preview ? (
              <iframe
                title="Aperçu de l'email" srcDoc={preview.html} sandbox=""
                className={device === "mobile" ? "w-[375px] h-[540px] bg-white rounded-xl border border-slate-300 shadow" : "w-full h-[540px] bg-white border-0"}
                data-testid="email-preview-iframe"
              />
            ) : (
              <div className="h-[540px] flex items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-violet-600" /></div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
